import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PortalTopbar from "../components/PortalTopbar";
import PortalAlert from "../components/PortalAlert";
import { useSignOutHref } from "../components/useSignOutHref";
import "../components/PortalButton.css";
import "./AccessPendingPage.css";

type ClinicAdminAction = "Add" | "Replace" | string;

type MyStatusResponse = {
  hasRequest: boolean;
  latest: null | {
    accessRequestId: number;
    requestedRole: string;
    clinicId: number | null;
    regionId: number | null;
    clinicAdminAction?: ClinicAdminAction | null;
    newClinicName?: string | null;
    newClinicAddressLine1?: string | null;
    newClinicAddressLine2?: string | null;
    newClinicCityId?: number | null;
    newClinicCityName?: string | null;
    newClinicPostalCode?: string | null;
    newClinicPhone?: string | null;
    newClinicWebsite?: string | null;
    existingClinicName?: string | null;
    existingClinicAddressLine1?: string | null;
    existingClinicAddressLine2?: string | null;
    existingClinicCityId?: number | null;
    existingClinicCityName?: string | null;
    existingClinicPostalCode?: string | null;
    existingClinicPhone?: string | null;
    existingClinicWebsite?: string | null;
    status: string;
    submittedAt: string;
    reviewedAt?: string | null;
  };
};

function fmt(val: unknown): string {
  const s = typeof val === "string" ? val.trim() : "";
  return s ? s : "—";
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function fmtPhone(val: string | null | undefined): string {
  const raw = String(val || "").trim();
  if (!raw) return "—";
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  }
  return raw;
}

function statusTone(statusRaw: string | null | undefined): "good" | "warn" | "bad" | "neutral" {
  const s = String(statusRaw || "").trim().toLowerCase();
  if (s === "approved") return "good";
  if (s === "pending") return "warn";
  if (s === "rejected" || s === "declined" || s === "cancelled") return "bad";
  return "neutral";
}

function KeyValueRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="apv-row">
      <dt className="apv-key">{label}</dt>
      <dd className="apv-val">{children}</dd>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="apv-skeleton" aria-hidden="true">
      <div className="apv-skel-row" />
      <div className="apv-skel-row" />
      <div className="apv-skel-row" />
      <div className="apv-skel-row" />
      <div className="apv-skel-row" />
    </div>
  );
}

export default function AccessPendingPage() {
  const nav = useNavigate();
  const signOutHref = useSignOutHref();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MyStatusResponse | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/access-requests/my-status", {
        credentials: "include",
      });
      const json = (await res.json().catch(() => null)) as MyStatusResponse | null;
      if (!res.ok) {
        const msg =
          (json as any)?.error ||
          (json as any)?.message ||
          `Request failed (${res.status})`;
        throw new Error(msg);
      }
      setData(json);
    } catch (err: any) {
      setError(err?.message || "Failed to load status");
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await load();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const latest = data?.latest || null;

  const isExistingClinicRequest = useMemo(
    () => latest?.clinicId !== null && latest?.clinicId !== undefined,
    [latest?.clinicId]
  );

  const isNewClinicRequest = !isExistingClinicRequest;

  const clinicAdminActionLabel = useMemo(() => {
    if (isExistingClinicRequest) {
      const a = String(latest?.clinicAdminAction || "").trim();
      if (!a) return "Assign first Clinic Admin";
      if (a === "Add") return "Add me as an additional Clinic Admin";
      if (a === "Replace") return "Replace existing Clinic Admin(s)";
      return a;
    }
    const a = String(latest?.clinicAdminAction || "").trim();
    if (!a) return "—";
    if (a === "Add") return "Add me as an additional Clinic Admin";
    if (a === "Replace") return "Replace existing Clinic Admin(s)";
    return a;
  }, [latest?.clinicAdminAction, isExistingClinicRequest]);

  const requestTypeLabel = useMemo(() => {
    if (!latest) return "—";
    return isExistingClinicRequest ? "Join an existing clinic" : "Create a new clinic";
  }, [latest, isExistingClinicRequest]);

  const statusPillTone = statusTone(latest?.status);

  return (
    <div className="apv-shell">
      <PortalTopbar showSignOut showPortalLogin={false} />

      <main className="apv-main" id="main">
        <div className="apv-card">
          {loading ? (
            <>
              <header className="apv-header">
                <h1 className="apv-title">Checking access status</h1>
                <p className="apv-subtitle">
                  Fetching your most recent access request from AcceptingMD.
                </p>
              </header>
              <LoadingSkeleton />
            </>
          ) : error ? (
            <>
              <header className="apv-header">
                <h1 className="apv-title">Could not load status</h1>
              </header>
              <PortalAlert tone="danger" title="Request failed">
                {error}
              </PortalAlert>
              <div className="apv-actions">
                <button type="button" className="pb-btn pb-btn--primary" onClick={load}>
                  Retry
                </button>
                <button
                  type="button"
                  className="pb-btn pb-btn--secondary"
                  onClick={() => nav("/portal/no-access")}
                >
                  Back
                </button>
              </div>

              <div className="apv-footer">
                <a href={signOutHref} className="apv-text-link">Sign out</a>
              </div>
            </>
          ) : !data || !data.hasRequest || !latest ? (
            <div className="apv-empty">
              <div className="apv-empty-icon" aria-hidden="true">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="9" y1="13" x2="15" y2="13" />
                  <line x1="9" y1="17" x2="13" y2="17" />
                </svg>
              </div>
              <h1 className="apv-title apv-title--center">No request on file</h1>
              <p className="apv-subtitle apv-subtitle--center">
                You don&apos;t have an access request under review right now.
                This can happen if you haven&apos;t submitted one yet, or if a
                previous request was cancelled or auto-expired.
              </p>

              <PortalAlert tone="info" title="Next step">
                Submit a Clinic Admin access request to get started. Super Admin
                review typically takes 1 to 2 business days.
              </PortalAlert>

              <div className="apv-empty-cta">
                <button
                  type="button"
                  className="pb-btn pb-btn--primary pb-btn--lg"
                  onClick={() => nav("/portal/request-access")}
                >
                  Request Clinic Admin access
                </button>
              </div>

              <div className="apv-footer">
                <a href={signOutHref} className="apv-text-link">Sign out</a>
                <span className="apv-footer-sep" aria-hidden="true">·</span>
                <Link to="/public" className="apv-text-link">Public site</Link>
              </div>
            </div>
          ) : (
            <>
              <header className="apv-header">
                <h1 className="apv-title">Clinic Admin request status</h1>
                <p className="apv-subtitle">
                  Your request is under review by a Super Admin. We may contact
                  the clinic to verify details before granting access.
                </p>
              </header>

              <section className="apv-section" aria-labelledby="apv-summary">
                <h2 id="apv-summary" className="apv-section-title">Summary</h2>
                <dl className="apv-kv">
                  <KeyValueRow label="Request ID">{latest.accessRequestId}</KeyValueRow>
                  <KeyValueRow label="Requested access">Clinic Admin</KeyValueRow>
                  <KeyValueRow label="Request type">{requestTypeLabel}</KeyValueRow>
                  {isExistingClinicRequest ? (
                    <KeyValueRow label="Clinic Admin access">{clinicAdminActionLabel}</KeyValueRow>
                  ) : null}
                  <KeyValueRow label="Status">
                    <span className={`apv-pill apv-pill--${statusPillTone}`}>
                      {fmt(latest.status)}
                    </span>
                  </KeyValueRow>
                  <KeyValueRow label="Submitted">{fmtDate(latest.submittedAt)}</KeyValueRow>
                  {latest.reviewedAt ? (
                    <KeyValueRow label="Last reviewed">{fmtDate(latest.reviewedAt)}</KeyValueRow>
                  ) : null}
                </dl>
              </section>

              {isExistingClinicRequest ? (
                <section className="apv-section" aria-labelledby="apv-selected">
                  <h2 id="apv-selected" className="apv-section-title">Selected clinic</h2>
                  <dl className="apv-kv">
                    <KeyValueRow label="Clinic ID">{latest.clinicId ?? "—"}</KeyValueRow>
                    {latest.existingClinicName ? (
                      <KeyValueRow label="Name">{fmt(latest.existingClinicName)}</KeyValueRow>
                    ) : null}
                    {latest.existingClinicAddressLine1 ? (
                      <KeyValueRow label="Address">
                        {fmt(latest.existingClinicAddressLine1)}
                        {latest.existingClinicAddressLine2
                          ? `, ${fmt(latest.existingClinicAddressLine2)}`
                          : ""}
                      </KeyValueRow>
                    ) : null}
                    {latest.existingClinicCityName || latest.existingClinicPostalCode ? (
                      <KeyValueRow label="City / Postal">
                        {fmt(latest.existingClinicCityName)}{" "}
                        {latest.existingClinicPostalCode
                          ? `(${fmt(latest.existingClinicPostalCode)})`
                          : ""}
                      </KeyValueRow>
                    ) : null}
                    <KeyValueRow label="Phone">{fmtPhone(latest.existingClinicPhone)}</KeyValueRow>
                    {latest.existingClinicWebsite ? (
                      <KeyValueRow label="Website">{fmt(latest.existingClinicWebsite)}</KeyValueRow>
                    ) : null}
                  </dl>
                </section>
              ) : null}

              {isNewClinicRequest ? (
                <section className="apv-section" aria-labelledby="apv-new">
                  <h2 id="apv-new" className="apv-section-title">New clinic details (draft)</h2>
                  <dl className="apv-kv">
                    <KeyValueRow label="Name">{fmt(latest.newClinicName)}</KeyValueRow>
                    <KeyValueRow label="Address">
                      {fmt(latest.newClinicAddressLine1)}
                      {latest.newClinicAddressLine2
                        ? `, ${fmt(latest.newClinicAddressLine2)}`
                        : ""}
                    </KeyValueRow>
                    <KeyValueRow label="City / Postal">
                      {fmt(latest.newClinicCityName)}{" "}
                      {latest.newClinicPostalCode
                        ? `(${fmt(latest.newClinicPostalCode)})`
                        : ""}
                    </KeyValueRow>
                    <KeyValueRow label="Phone">{fmtPhone(latest.newClinicPhone)}</KeyValueRow>
                    {latest.newClinicWebsite ? (
                      <KeyValueRow label="Website">{fmt(latest.newClinicWebsite)}</KeyValueRow>
                    ) : null}
                  </dl>
                </section>
              ) : null}

              <div className="apv-actions">
                <button type="button" className="pb-btn pb-btn--primary" onClick={load}>
                  Refresh status
                </button>
                <button
                  type="button"
                  className="pb-btn pb-btn--secondary"
                  onClick={() => nav("/portal/no-access")}
                >
                  Back
                </button>
              </div>

              <div className="apv-footer">
                <a href={signOutHref} className="apv-text-link">Sign out</a>
                <span className="apv-footer-sep" aria-hidden="true">·</span>
                <Link to="/public" className="apv-text-link">Public site</Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
